import { dashboardOrgMcpServerSchema } from "@arti/schemas/dashboard";
import { hasValue } from "@arti/utilities";
import { useCallback, useEffect, useState } from "react";

import {
  type DashboardOrgMcpServer,
  listOrganizationMcpServersAsUser,
  registerAnMcpServerForTheOrganizationAsUser,
  removeAnOrganizationMcpServerAsUser,
  updateAnOrganizationMcpServerAsUser,
} from "../dashboard-client";

export interface McpHeaderDraft {
  name: string;
  value: string;
  isSecret: boolean;
  /**
   * True when the server already stores a value for this header. The draft then
   * carries an empty `value` meaning "leave it as it is", which is what lets an
   * admin edit a URL without re-typing credentials they cannot read back.
   */
  storedValueSet: boolean;
}

export interface McpServerDraft {
  slug: string;
  displayName: string;
  description: string;
  url: string;
  status: "active" | "disabled";
  headers: McpHeaderDraft[];
}

interface State {
  servers: DashboardOrgMcpServer[];
  loading: boolean;
  error: string | null;
}

/**
 * Errors from these endpoints are meant to be shown verbatim: the API answers
 * with a message naming the offending header or the conflicting name, and
 * replacing that with "Request failed" would throw away the only part an admin
 * can act on.
 */
function describeFailure(error: unknown, fallback: string): string {
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return error.message;
  }
  if (
    typeof error === "object" &&
    error !== null &&
    "error" in error &&
    typeof error.error === "string"
  ) {
    return error.error;
  }
  return fallback;
}

/**
 * A 200 is not a promise of the body you asked for. When this endpoint is not
 * deployed the dev server answers 200 with index.html, and trusting the shape
 * cost a white screen: the generated client does not throw, `servers` came back
 * undefined, and the first `.length` in render took the whole dashboard down.
 * An unrecognisable body is a failure to report, not a list to render.
 */
export function readServersResponse(
  data: unknown,
): DashboardOrgMcpServer[] | null {
  if (typeof data !== "object" || data === null || !("servers" in data)) {
    return null;
  }
  const { servers } = data;
  if (!Array.isArray(servers)) {
    return null;
  }
  return servers
    .map(
      (server: unknown) => dashboardOrgMcpServerSchema.safeParse(server).data,
    )
    .filter(hasValue);
}

function toHeaderPayload(headers: McpHeaderDraft[]) {
  return headers
    .filter((header) => header.name.trim().length > 0)
    .map((header) => ({
      is_secret: header.isSecret,
      name: header.name.trim(),
      // An untouched stored secret is submitted by name only; sending an empty
      // string would ask the API to store an empty credential.
      ...(header.value.length > 0 ? { value: header.value } : {}),
    }));
}

export function useOrgMcpServers(organizationId: string | null) {
  const [state, setState] = useState<State>({
    error: null,
    loading: false,
    servers: [],
  });

  const refresh = useCallback(async () => {
    if (!organizationId) {
      setState({ error: null, loading: false, servers: [] });
      return;
    }
    setState((previous) => ({ ...previous, error: null, loading: true }));
    try {
      const { data } = await listOrganizationMcpServersAsUser();
      const servers = readServersResponse(data);
      if (!servers) {
        setState({
          error:
            "The MCP servers endpoint returned an unexpected response. It may not be deployed in this environment.",
          loading: false,
          servers: [],
        });
        return;
      }
      setState({ error: null, loading: false, servers });
    } catch (error) {
      setState((previous) => ({
        ...previous,
        error: describeFailure(error, "Failed to load MCP servers"),
        loading: false,
      }));
    }
  }, [organizationId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const create = useCallback(
    async (draft: McpServerDraft): Promise<string | null> => {
      try {
        await registerAnMcpServerForTheOrganizationAsUser({
          body: {
            description: draft.description.trim() || null,
            display_name: draft.displayName.trim(),
            headers: toHeaderPayload(draft.headers),
            slug: draft.slug.trim(),
            status: draft.status,
            url: draft.url.trim(),
          },
        });
        await refresh();
        return null;
      } catch (error) {
        return describeFailure(error, "Failed to register the MCP server");
      }
    },
    [refresh],
  );

  const update = useCallback(
    async (id: string, draft: McpServerDraft): Promise<string | null> => {
      try {
        await updateAnOrganizationMcpServerAsUser({
          body: {
            description: draft.description.trim() || null,
            display_name: draft.displayName.trim(),
            headers: toHeaderPayload(draft.headers),
            status: draft.status,
            url: draft.url.trim(),
          },
          path: { mcpServerId: id },
        });
        await refresh();
        return null;
      } catch (error) {
        return describeFailure(error, "Failed to update the MCP server");
      }
    },
    [refresh],
  );

  const remove = useCallback(
    async (id: string): Promise<string | null> => {
      try {
        await removeAnOrganizationMcpServerAsUser({
          path: { mcpServerId: id },
        });
        await refresh();
        return null;
      } catch (error) {
        return describeFailure(error, "Failed to remove the MCP server");
      }
    },
    [refresh],
  );

  return { ...state, create, refresh, remove, update };
}

/** Turns a stored server back into an editable draft. */
export function draftFromServer(server: DashboardOrgMcpServer): McpServerDraft {
  return {
    description: server.description ?? "",
    displayName: server.display_name,
    headers: server.headers.map((header) => ({
      isSecret: header.is_secret,
      name: header.name,
      storedValueSet: header.value_set,
      value: header.is_secret ? "" : (header.value ?? ""),
    })),
    slug: server.slug,
    status: server.status,
    url: server.url,
  };
}

export function emptyDraft(): McpServerDraft {
  return {
    description: "",
    displayName: "",
    headers: [],
    slug: "",
    status: "active",
    url: "",
  };
}

/**
 * A slug namespaces the server's tools for an agent, so it has to be safe in a
 * tool name. Deriving it from the display name keeps the common case to one
 * field while leaving it editable, because it is the name agents will see.
 */
export function slugFromDisplayName(displayName: string): string {
  return (
    displayName
      .toLowerCase()
      .replace(/[^a-z0-9]+/gu, "_")
      // Must START with a letter, so strip every leading digit AND the
      // separator a digit leaves behind: "42 Monitoring" has to become
      // "monitoring", not "_monitoring", which the API would reject.
      .replace(/^[^a-z]+/u, "")
      .replace(/_+$/u, "")
      .slice(0, 64)
  );
}
